import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { bloodTestsAPI } from '../../services/api'
import { useStartHealthCycle } from './useStartHealthCycle'
import { formatDate } from './cycleStatus'

// VH-247 stage 2: its own file, used by the Health cycles tab and the cycle home page.
export function StartCycleForm({ initialTestId = '', bare = false }) {
  const { t, i18n } = useTranslation()
  const [bloodTestId, setBloodTestId] = useState(initialTestId ? String(initialTestId) : '')
  const [questions, setQuestions] = useState('')
  const { start, discardAndRestart, openExisting, conflict, isPending, error } = useStartHealthCycle()
  const { data: tests = [] } = useQuery({
    queryKey: ['bloodTests'],
    queryFn: () => bloodTestsAPI.getAll(),
    select: (response) => (Array.isArray(response.data) ? response.data : response.data?.items ?? []),
  })
  const analysed = tests.filter((test) => test.status === 'analyzed')

  const submit = (e) => {
    e.preventDefault()
    if (!bloodTestId) return
    start({
      bloodTestId: Number(bloodTestId),
      questions: questions.split('\n').map((q) => q.trim()).filter(Boolean).slice(0, 10),
    })
  }

  return (
    <form onSubmit={submit} className={bare ? '' : 'card'} data-testid="start-cycle-form">
      <div className={bare ? 'space-y-3' : 'card-body space-y-3'}>
        {!bare && <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.start.title')}</h3>}
        <label className="block text-sm text-gray-700">
          {t('healthCycle.start.bloodTest')}
          <select
            className="mt-1 block w-full text-sm"
            value={bloodTestId}
            onChange={(e) => setBloodTestId(e.target.value)}
          >
            <option value="">{t('healthCycle.start.pickTest')}</option>
            {analysed.map((test) => (
              <option key={test.id} value={test.id}>
                {formatDate(test.test_date, i18n.language)}
                {test.lab_name ? ` — ${test.lab_name}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-gray-700">
          {t('healthCycle.start.questions')}
          <textarea
            className="mt-1 block w-full text-sm"
            rows={2}
            value={questions}
            onChange={(e) => setQuestions(e.target.value)}
            placeholder={t('healthCycle.start.questionsPlaceholder')}
          />
        </label>
        {error && <p className="text-xs text-red-600" role="alert">{t(`healthCycle.errors.${error}`, t('healthCycle.errors.generic'))}</p>}
        {conflict && (
          <div className="border border-yellow-200 bg-yellow-50 rounded-md p-3 text-sm">
            <p className="text-yellow-800">{t('healthCycle.start.draftExists')}</p>
            <div className="flex gap-2 mt-2">
              <button type="button" className="btn-outline text-xs" onClick={openExisting}>{t('healthCycle.start.openDraft')}</button>
              <button type="button" className="btn-outline text-xs" onClick={discardAndRestart}>{t('healthCycle.start.discardDraft')}</button>
            </div>
          </div>
        )}
        <button type="submit" className="btn-primary text-sm" disabled={!bloodTestId || isPending}>
          {isPending ? t('healthCycle.start.starting') : t('healthCycle.start.button')}
        </button>
      </div>
    </form>
  )
}

export default StartCycleForm
